import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Grid2 } from '@mui/material';
import axios from 'axios';
import CONFIG from '../config/config';
import ReCAPTCHA from 'react-google-recaptcha';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: '',
    });
    const [captchaToken, setCaptchaToken] = useState(null);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }
        if (!formData.message.trim()) newErrors.message = 'Message is required';
        if (!captchaToken) newErrors.captcha = 'Please complete the reCAPTCHA';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (validateForm()) {
            setIsSubmitting(true);
            try {
                const url = `${CONFIG.API.BASE_URL}/contact`;
                const response = await axios.post(url, {
                    name: formData.name,
                    email: formData.email,
                    message: formData.message,
                    accessToken: CONFIG.API.ACCESS_TOKEN,
                    captchaToken,
                });

                if (response.status === 200) {
                    alert('Thank you for reaching out! We will get back to you soon.');
                    setFormData({ name: '', email: '', message: '' });
                    setErrors({});
                    setCaptchaToken(null); // Reset captcha
                } else {
                    alert('Failed to send your message. Please try again later.');
                }
            } catch (error) {
                console.error('Error sending contact form:', error);
                alert('An error occurred while sending your message. Please try again.');
            } finally {
                setIsSubmitting(false);
            }
        } else {
            console.log('Form has errors:', errors);
        }
    };

    const handleCaptchaChange = (token) => {
        setCaptchaToken(token);
    };

    const handleCaptchaExpired = () => {
        setCaptchaToken(null);
    };

    return (
        <Box
            sx={{
                padding: '16px 16px 16px',
                backgroundColor: '#f9f9f9',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'left',
            }}
        >
            <Typography
                variant="h4"
                sx={{
                    fontWeight: 500,
                    marginBottom: '22px',
                    textAlign: 'left',
                }}
            >
                Contact Us
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    color: '#555',
                    marginBottom: '32px',
                    lineHeight: 1.8,
                    textAlign: 'left',
                }}
            >
                We value your input! Whether you have a question, a suggestion, or just want to say hello, we’re here for
                you. Please don’t hesitate to reach out using the form below.
            </Typography>

            <form onSubmit={handleSubmit}>
                <Grid2 container gap={2} sx={{ width: '100%', margin: '0 auto', justifyContent: 'flex-start' }}>
                    <Grid2 item sx={{ width: { xs: '100%', sm: '50%' } }}>
                        <TextField
                            label="Your Name"
                            name="name"
                            variant="outlined"
                            fullWidth
                            required
                            autoComplete="off"
                            value={formData.name}
                            onChange={handleChange}
                            error={!!errors.name}
                            helperText={errors.name}
                            disabled={isSubmitting}
                        />
                    </Grid2>
                    <Grid2 item sx={{ width: { xs: '100%', sm: '50%' } }}>
                        <TextField
                            label="Your Email"
                            name="email"
                            type="email"
                            variant="outlined"
                            fullWidth
                            required
                            autoComplete="off"
                            value={formData.email}
                            onChange={handleChange}
                            error={!!errors.email}
                            helperText={errors.email}
                            disabled={isSubmitting}
                        />
                    </Grid2>
                    <Grid2 item sx={{ width: '100%' }}>
                        <TextField
                            label="Message"
                            name="message"
                            variant="outlined"
                            fullWidth
                            required
                            autoComplete="off"
                            multiline
                            rows={4}
                            value={formData.message}
                            onChange={handleChange}
                            error={!!errors.message}
                            helperText={errors.message}
                            disabled={isSubmitting}
                        />
                    </Grid2>
                    <Grid2 item sx={{ width: '100%' }}>
                        <ReCAPTCHA
                            sitekey={CONFIG.GOOGLE.SITE_KEY_V2}
                            onChange={handleCaptchaChange}
                            onExpired={handleCaptchaExpired}
                        />
                        {errors.captcha && <Typography color="error">{errors.captcha}</Typography>}
                    </Grid2>
                    <Grid2 item xs={12}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            sx={{ padding: '12px 24px', fontWeight: 600, fontSize: '16px', textTransform: 'none' }}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                        </Button>
                    </Grid2>
                </Grid2>
            </form>
        </Box>
    );
};

export default Contact;